import React, { useRef, useState, Suspense } from 'react';
import {
  Box, Typography, Button, Stack, CircularProgress,
  Switch, FormControlLabel, TextField,
} from '@mui/material';
import { Upload, FileText, FilePen, FilePlus } from 'lucide-react';

const LazyDocxEditorDialog = React.lazy(() =>
  import('../../../business/DocumentWidget/DocxEditorDialog')
);

const navy = '#1e3a5f';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword',
];
const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB

const detectDocumentType = (file) => {
  if (file.type === 'application/pdf') return 'pdf';
  if (
    file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
    file.type === 'application/msword' ||
    file.name.endsWith('.docx') ||
    file.name.endsWith('.doc')
  ) return 'docx';
  return null;
};

const DocumentStep = ({ formData, onChange, onUploadImage }) => {
  const documentUrl = formData?.documentUrl || '';
  const documentType = formData?.documentType || '';
  const fileName = formData?.fileName || '';
  const pageNumber = formData?.pageNumber || 1;
  const autoScroll = formData?.autoScroll || false;
  const scrollInterval = formData?.scrollInterval || 10;

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [createNew, setCreateNew] = useState(false);
  const fileRef = useRef(null);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !onUploadImage) return;

    const docType = detectDocumentType(file);
    if (!docType) {
      setUploadError('יש להעלות קובץ PDF או Word בלבד.');
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setUploadError('גודל הקובץ חייב להיות עד 15MB.');
      return;
    }

    setUploading(true);
    setUploadError('');
    try {
      const url = await onUploadImage(file);
      onChange({
        documentUrl: url,
        documentType: docType,
        fileName: file.name,
        pageNumber: 1,
        autoScroll: false,
        scrollInterval: 10,
      });
    } catch (err) {
      setUploadError('שגיאה בהעלאת הקובץ — נסה שוב.');
    } finally {
      setUploading(false);
    }
  };

  const handleEditorSave = (newUrl) => {
    onChange({
      documentUrl: newUrl,
      documentType: 'docx',
      fileName: fileName || 'מסמך חדש.docx',
    });
    setEditorOpen(false);
    setCreateNew(false);
  };

  const handleCreateNew = () => {
    setCreateNew(true);
    setEditorOpen(true);
  };

  return (
    <Box dir="rtl">
      <Typography sx={{ fontWeight: 700, color: navy, fontSize: '1rem', mb: 0.5 }}>
        העלאת מסמך
      </Typography>
      <Typography sx={{ fontSize: '0.8rem', color: '#6b7280', mb: 2.5, lineHeight: 1.5 }}>
        העלה קובץ PDF או Word מהמחשב, או צור מסמך Word חדש.
      </Typography>

      {/* Upload button */}
      <Box sx={{ bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.25}>
          <Upload size={15} color="#6b7280" />
          <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
            העלאת קובץ
          </Typography>
        </Stack>

        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.docx,.doc"
          style={{ display: 'none' }}
          onChange={handleFileChange}
        />

        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            startIcon={uploading ? <CircularProgress size={14} /> : <Upload size={14} />}
            sx={{
              borderColor: '#e5e7eb', color: '#374151', textTransform: 'none',
              fontSize: '0.83rem', borderRadius: '8px',
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
            }}
          >
            {uploading ? 'מעלה...' : 'בחר קובץ'}
          </Button>

          <Button
            variant="outlined"
            onClick={handleCreateNew}
            startIcon={<FilePlus size={14} />}
            sx={{
              borderColor: '#e5e7eb', color: '#374151', textTransform: 'none',
              fontSize: '0.83rem', borderRadius: '8px',
              '&:hover': { borderColor: navy, color: navy, bgcolor: '#f0f4fa' },
            }}
          >
            צור מסמך Word חדש
          </Button>
        </Stack>

        {uploadError && (
          <Typography sx={{ fontSize: '0.78rem', color: '#ef4444', mt: 1 }}>
            {uploadError}
          </Typography>
        )}
      </Box>

      {/* File info + settings */}
      {documentUrl && (
        <Box sx={{ mt: 2.5, bgcolor: 'white', border: '1px solid #e5e7eb', borderRadius: 2, p: 2.5 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
            <FileText size={18} color={navy} />
            <Typography sx={{ fontWeight: 600, color: '#374151', fontSize: '0.875rem' }}>
              {fileName}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#9ca3af', ml: 'auto' }}>
              {documentType === 'pdf' ? 'PDF' : 'Word'}
            </Typography>
          </Stack>

          {/* DOCX: Edit button */}
          {documentType === 'docx' && (
            <Button
              variant="outlined"
              onClick={() => setEditorOpen(true)}
              startIcon={<FilePen size={14} />}
              sx={{
                borderColor: '#e5e7eb', color: navy, textTransform: 'none',
                fontSize: '0.83rem', borderRadius: '8px', mb: 1.5,
                '&:hover': { borderColor: navy, bgcolor: '#f0f4fa' },
              }}
            >
              ערוך מסמך
            </Button>
          )}

          {/* Page settings (PDF & DOCX) */}
          {(documentType === 'pdf' || documentType === 'docx') && (
            <Box sx={{ mt: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={autoScroll}
                    onChange={(e) => onChange({ autoScroll: e.target.checked })}
                    size="small"
                    sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: navy }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: navy } }}
                  />
                }
                label={<Typography sx={{ fontSize: '0.82rem', color: '#374151' }}>גלילה אוטומטית בין עמודים</Typography>}
                sx={{ mb: 1, mr: 0 }}
              />

              {autoScroll ? (
                <TextField
                  size="small"
                  type="number"
                  label="שניות בין עמודים"
                  value={scrollInterval}
                  onChange={(e) => onChange({ scrollInterval: Math.max(1, parseInt(e.target.value) || 10) })}
                  inputProps={{ min: 1, max: 120 }}
                  sx={{ width: 160, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
                />
              ) : (
                <TextField
                  size="small"
                  type="number"
                  label="מספר עמוד להצגה"
                  value={pageNumber}
                  onChange={(e) => onChange({ pageNumber: Math.max(1, parseInt(e.target.value) || 1) })}
                  inputProps={{ min: 1 }}
                  sx={{ width: 160, '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' } }}
                />
              )}
            </Box>
          )}
        </Box>
      )}

      {/* DOCX Editor Dialog */}
      {editorOpen && (
        <Suspense fallback={null}>
          <LazyDocxEditorDialog
            open={editorOpen}
            documentUrl={createNew ? null : documentUrl}
            onSave={handleEditorSave}
            onClose={() => { setEditorOpen(false); setCreateNew(false); }}
            onUploadDocument={onUploadImage}
          />
        </Suspense>
      )}
    </Box>
  );
};

export default DocumentStep;
